import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { Send } from 'lucide-react';

export default function ContactForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [team, setTeam] = useState('');
  const [organization, setOrganization] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [recipientEmail, setRecipientEmail] = useState('support@hoofdnaarhart.nl');

  useEffect(() => {
    const fetchContactEmail = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'contact_email')
          .single();

        if (!error && data) {
          setRecipientEmail(data.value);
        }
      } catch (error) {
        console.error('Error fetching contact email:', error);
      }
    };

    fetchContactEmail();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName || !lastName || !email || !organization) {
      setSubmitStatus({
        type: 'error',
        message: 'Vul alle verplichte velden in.',
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      // Call the Supabase Edge Function to send the email
      const { data, error } = await supabase.functions.invoke('contact-form', {
        body: {
          name: `${firstName} ${lastName}`,
          email,
          role,
          team,
          organization,
          message: `Aanvraag voor organisatie abonnement van ${firstName} ${lastName} (${role}) van team ${team} bij ${organization}`,
          recipient: recipientEmail,
        },
      });

      if (error) throw error;

      // Clear the form
      setFirstName('');
      setLastName('');
      setEmail('');
      setRole('');
      setTeam('');
      setOrganization('');

      setSubmitStatus({
        type: 'success',
        message:
          'Je aanvraag is verzonden. We nemen zo snel mogelijk contact met je op.',
      });
    } catch (error) {
      console.error('Error sending contact form:', error);
      setSubmitStatus({
        type: 'error',
        message:
          'Er is een fout opgetreden bij het verzenden van je aanvraag. Probeer het later opnieuw.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="firstName">Voornaam</Label>
        <Input
          id="firstName"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Voornaam"
          disabled={isSubmitting}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="lastName">Achternaam</Label>
        <Input
          id="lastName"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Achternaam"
          disabled={isSubmitting}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">E-mailadres</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@voorbeeld.nl"
          disabled={isSubmitting}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="role">Functie</Label>
        <Input
          id="role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="Functie"
          disabled={isSubmitting}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="team">Team</Label>
        <Input
          id="team"
          value={team}
          onChange={(e) => setTeam(e.target.value)}
          placeholder="Team"
          disabled={isSubmitting}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="organization">Organisatie</Label>
        <Input
          id="organization"
          value={organization}
          onChange={(e) => setOrganization(e.target.value)}
          placeholder="Organisatie"
          disabled={isSubmitting}
          required
        />
      </div>

      {submitStatus.type && (
        <div
          className={`p-3 rounded-md ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}
        >
          {submitStatus.message}
        </div>
      )}

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? (
          'Verzenden...'
        ) : (
          <>
            <Send className="mr-2 h-4 w-4" /> Versturen
          </>
        )}
      </Button>
    </form>
  );
}
